import { gql } from '@apollo/client';
import {
  Download,
  DownloadStatus,
  DownloadTemplate,
} from 'spw-programming-projects/downloads/types';
import { saveAs } from 'file-saver';
import { sanitizeFileName } from 'spw-shared/utils';
import { graphqlClient, httpClient } from './utils';

const TIMEOUT = 60000;

export const createDownloadTemplate = (
  downloadTemplate: Partial<DownloadTemplate>
) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateDownloadTemplate(
        $downloadTemplate: DownloadTemplateArgs!
      ) {
        downloadTemplate: createDownloadTemplate(
          downloadTemplate: $downloadTemplate
        ) {
          id
          name
          downloadConfigs {
            downloadConfigType
            detailedConfig {
              shouldRowsShowOpen
              shouldRowsShowClosed
            }
            scenarioId
            totalProgramId
          }
        }
      }
    `,
    variables: {
      downloadTemplate,
    },
  });

export const updateDownloadTemplate = (
  downloadTemplate: Partial<DownloadTemplate>
) =>
  graphqlClient.mutate<{ downloadTemplate: Partial<DownloadTemplate> }>({
    mutation: gql`
      mutation UpdateDownloadTemplate(
        $downloadTemplate: DownloadTemplateArgs!
      ) {
        downloadTemplate: updateDownloadTemplate(
          downloadTemplate: $downloadTemplate
        ) {
          id
          name
          downloadConfigs {
            downloadConfigType
            detailedConfig {
              shouldRowsShowOpen
              shouldRowsShowClosed
            }
            scenarioId
            totalProgramId
          }
        }
      }
    `,
    variables: {
      downloadTemplate,
    },
  });

export const getDownloadTemplates = (projectId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetDownloadTemplates($projectId: Int!) {
        downloadTemplates(projectId: $projectId) {
          id
          name
          downloadName
          formatConfigs {
            format
            showClientFacingNames
            showRsf
          }
          downloadConfigs {
            downloadConfigType
            downloadFileType
            detailedConfig {
              shouldRowsShowOpen
              shouldRowsShowClosed
            }
            scenarioId
            totalProgramId
          }
          user {
            id
            name
          }
          insertedAt
        }
      }
    `,
    variables: { projectId },
  });

export const getDownloadTemplate = (id: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetDownloadTemplate($id: Int!) {
        downloadTemplate(id: $id) {
          id
          name
          formatConfigs {
            format
            showClientFacingNames
            showRsf
          }
          downloadConfigs {
            downloadConfigType
            downloadFileType
            detailedConfig {
              shouldRowsShowOpen
              shouldRowsShowClosed
            }
            scenarioId
            totalProgramId
          }
          user {
            name
            id
          }
          insertedAt
        }
      }
    `,
    variables: { id },
  });

export const deleteDownloadTemplate = (downloadTemplateId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation DeleteDownloadTemplate($downloadTemplateId: Int!) {
        deleteDownloadTemplate(id: $downloadTemplateId) {
          id
        }
      }
    `,
    variables: {
      downloadTemplateId,
    },
  });

export const getDownloads = (filters?: {
  status?: string;
  projectId?: number;
  ids?: number[];
}) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetDownloads($filters: DownloadFilters) {
        downloads: getDownloads(filters: $filters) {
          id
          name
          status
          numberOfFiles
          insertedAt
        }
      }
    `,
    variables: {
      filters,
    },
  });

export const getDownloadsAndTemplatesInProgress = (filters?: {
  projectId?: number;
  ids?: number[];
}) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetDownloadsAndTemplatesInProgress($filters: DownloadFilters) {
        downloads: getDownloadsAndTemplate(filters: $filters) {
          download {
            id
            name
            status
            numberOfFiles
            insertedAt
          }
          template {
            id
          }
        }
      }
    `,
    variables: {
      filters: { ...filters, status: DownloadStatus.IN_PROGRESS },
    },
  });

export const getDownloadsAndTemplate = (filters?: {
  status?: string;
  projectId?: number;
  ids?: number[];
}) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetDownloadsAndTemplate($filters: DownloadFilters) {
        downloads: getDownloadsAndTemplate(filters: $filters) {
          download {
            id
            name
            status
            numberOfFiles
            insertedAt
          }
          template {
            id
            name
            downloadName
            projectId
            downloadConfigs {
              downloadConfigType
              downloadFileType
              detailedConfig {
                shouldRowsShowOpen
                shouldRowsShowClosed
              }
              scenarioId
              totalProgramId
            }
            user {
              name
              id
            }
            insertedAt
          }
        }
      }
    `,
    variables: {
      filters,
    },
  });

export const getNumberOfDownloadsInProgress = () =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      query GetNumberOfDownloadsInProgress {
        numberOfDownloadsInProgress: getNumberOfDownloadsInProgress
      }
    `,
  });

export const cancelDownloadsInProgress = () =>
  graphqlClient.mutate({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation CancelDownloadsInProgress {
        cancelDownloadsInProgress: cancelDownloadsInProgress
      }
    `,
  });

export const createDownload = (download: Partial<Download>) =>
  graphqlClient.mutate({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation CreateDownload($download: DownloadInput!) {
        download: createDownload(download: $download) {
          id
          name
          status
          numberOfFiles
          insertedAt
        }
      }
    `,
    variables: {
      download,
    },
  });

export const createDownloadFromTemplate = (
  templateId: number,
  downloadName?: string
) =>
  graphqlClient.mutate({
    mutation: gql`
      mutation CreateDownloadFromTemplate(
        $templateId: Int!
        $downloadName: String
      ) {
        download: createDownloadFromTemplate(
          templateId: $templateId
          downloadName: $downloadName
        ) {
          id
          name
          status
          numberOfFiles
          insertedAt
        }
      }
    `,
    variables: {
      templateId,
      downloadName,
    },
  });

export const deleteDownload = (id: number) =>
  graphqlClient.mutate({
    fetchPolicy: 'no-cache',
    mutation: gql`
      mutation DeleteDownload($id: Int!) {
        deletedDownload: deleteDownload(id: $id) {
          id
        }
      }
    `,
    variables: { id },
  });

export const downloadZipFile = (download: Partial<Download>) => {
  const {
    location: { protocol, host },
  } = window;

  const hostName = `${protocol}//${host}`;
  return httpClient({
    method: 'GET',
    url: `${hostName}/downloads/${download.id}`,
    responseType: 'blob',
    timeout: TIMEOUT,
  }).then((response) => {
    saveAs(response.data, `${sanitizeFileName(download.name)}.zip`);
  });
};
